import React, { Children, useEffect, useState } from 'react';

import {
  ClickAwayListener,
  Divider,
  ListItem,
  ListItemButton,
  Paper,
  Popper,
  styled,
} from '@mui/material';
import PropTypes from 'prop-types';

const Popper_Wrapper = styled(Popper)(() => ({
  marginLeft: '8px',
  marginBottom: '15px',
  zIndex: 99999,
}));

const Paper_Wrapper = styled(Paper)(({ theme }) => ({
  padding: 0,
  borderRadius: theme.spacing(3),
  boxShadow: '0px 6px 15px 0px rgba(23, 33, 52, 0.15)',
  overflow: 'hidden',
}));

export const ListItemButton_Styled = styled(ListItemButton)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
}));

export const ListItem_Styled = styled(ListItem)(({ theme }) => ({
  padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
}));

const FCList = ({
  anchor,
  children,
  placement = 'bottom-start',
  noDivider = false,
  isOpen,
  onClose = () => {},
  attacheEventToAnchor,
}) => {
  const arrayChildren = Children.toArray(children);
  const [isListOpen, setIsListOpen] = useState(false);

  const toggleDropdown = () => {
    setIsListOpen(prevOpen => !prevOpen);
  };

  const closeDropdown = () => {
    setIsListOpen(false);
    onClose();
  };

  useEffect(() => {
    if (anchor) setIsListOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    const handleKeydown = event => {
      if (event.key === 'Escape' && (isOpen || isListOpen)) {
        setIsListOpen(false);
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeydown);
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [isOpen, isListOpen]);

  useEffect(() => {
    if (!attacheEventToAnchor) return;
    const anchorElement = typeof anchor === 'string' ? document.querySelector(anchor) : anchor;
    if (anchorElement) {
      anchorElement.addEventListener('click', toggleDropdown);
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (anchorElement) {
        anchorElement.removeEventListener('click', toggleDropdown);
      }
    };
  }, [anchor]);

  return (
    <Popper_Wrapper
      open={isListOpen}
      placement={placement}
      anchorEl={typeof anchor === 'string' ? document.querySelector(anchor) : anchor}
    >
      <ClickAwayListener onClickAway={closeDropdown}>
        <Paper_Wrapper>
          {Children.map(arrayChildren, (child, index) => {
            const isLast = index === arrayChildren.length - 1;

            return (
              <React.Fragment key={arrayChildren.indexOf(child)}>
                {React.cloneElement(child, { onCloseDropdown: closeDropdown })}
                {!isLast && !noDivider && <Divider variant="inset" sx={{ ml: 0 }} />}
              </React.Fragment>
            );
          })}
        </Paper_Wrapper>
      </ClickAwayListener>
    </Popper_Wrapper>
  );
};

FCList.propTypes = {
  anchor: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Element)]),
  placement: PropTypes.oneOf(['bottom-start', 'bottom-end', 'top-start', 'top-end']),
  children: PropTypes.node.isRequired,
  noDivider: PropTypes.bool,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  attacheEventToAnchor: PropTypes.bool,
};

const ListItemButtonComponent = ({ children, onCloseDropdown, onClick, ...props }) => (
  <ListItemButton_Styled
    {...props}
    onClick={() => {
      if (onCloseDropdown) {
        onCloseDropdown();
      }
      if (onClick) {
        onClick();
      }
    }}
  >
    {children}
  </ListItemButton_Styled>
);

ListItemButtonComponent.propTypes = {
  children: PropTypes.node.isRequired,
  onCloseDropdown: PropTypes.func,
  onClick: PropTypes.func,
};

FCList.ListItem = ListItem_Styled;
FCList.ListItemButton = ListItemButtonComponent;

export default FCList;
